/* ========================================
   FILTER UTILITIES
   Configuration, utility functions, and caching
   Extracted from collection-filter.js
   ======================================== */

(function() {
  'use strict';

  // ========================================
  // CONFIGURATION & CONSTANTS
  // ========================================
  
  const CONFIG = {
    localStorageKeys: {
      filterOpen: 'collection_filter_open',
      filterValue: 'filter_value',
      accordionStates: 'accordion_states',
      selectedFilters: 'selected_filters'
    },
    selectors: {
      showFilterBtn: '#show_filter',
      collectionFilter: '.collection-filter',
      closeFilterBtn: '.filter-close-button',
      blurCover: '.desktop-blur-cover',
      filterTags: '.collection-filter__tag a',
      filterTagItems: '.collection-filter__tag',
      sortSelect: '#sort-by',
      filterBarContainer: '.filter--bar__container',
      filterBarCarousel: '.filter-bar--carousel',
      filterCollectionBoxes: '.filter-collection-box',
      accordionDetails: '[data-accordion-details]',
      filterLoadingOverlay: '#filter-loading-overlay',
      collectionLoadingOverlay: '#collection-loading-overlay',
      productsWrapper: '.collection__products-wrapper',
      collectionMain: '.collection__main',
      selectedFiltersContainer: '#selected-filters-container',
      selectedFiltersWrapper: '#selected-filters-wrapper',
      selectedFiltersList: '#selected-filters-list',
      clearAllFiltersBtn: '#clear-all-filters',
      applyButton: '#filter-apply-button',
      applyButtonCount: '#filter-apply-button-count',
      applyContainer: '#filter-apply-container',
      applyButtonText: '.filter-apply-button-text',
      collectionElement: '[data-collection-handle], .collection',
      productCard: 'product-card, .product-card, [data-product-card], .card'
    },
    breakpoints: {
      desktop: 990,
      mobile: 768
    }
  };

  // ========================================
  // UTILITY FUNCTIONS
  // ========================================
  
  const Utils = {
    isDesktop: () => window.innerWidth >= CONFIG.breakpoints.desktop,
    isMobile: () => window.innerWidth <= CONFIG.breakpoints.mobile,
    
    getLocalStorage: (key) => localStorage.getItem(key),
    setLocalStorage: (key, value) => localStorage.setItem(key, value),
    removeLocalStorage: (key) => localStorage.removeItem(key),
    
    log: () => {},
    error: (message, data = null) => {
      console.error(`[CollectionFilter] ${message}`, data || '');
    },
    warn: () => {},
    
    showError: (message) => {
      // Show user-friendly error message
      const errorToast = document.createElement('div');
      errorToast.className = 'filter-error-toast';
      errorToast.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #ff4444; color: white; padding: 16px 24px; border-radius: 8px; z-index: 1000000; box-shadow: 0 4px 12px rgba(0,0,0,0.15); max-width: 400px;';
      errorToast.textContent = message;
      document.body.appendChild(errorToast);
      
      // Use requestAnimationFrame for smooth fade-out animation
      requestAnimationFrame(() => {
        setTimeout(() => {
          errorToast.style.opacity = '0';
          errorToast.style.transition = 'opacity 0.3s';
          // Use requestAnimationFrame for removal after transition
          requestAnimationFrame(() => {
            setTimeout(() => errorToast.remove(), 300);
          });
        }, 5000);
      });
      
      Utils.error(message);
    },

    // Debounce function for performance optimization (preserves context)
    debounce: function(func, delay) {
      var timeoutId;
      return function() {
        var context = this;
        var args = arguments;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(function() {
          func.apply(context, args);
        }, delay);
      };
    },

    // Throttle function for scroll events (preserves context)
    throttle: function(func, delay) {
      var inThrottle;
      return function() {
        var context = this;
        var args = arguments;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(function() {
            inThrottle = false;
          }, delay);
        }
      };
    },

    // Efficient DOM update using DocumentFragment
    updateDOMEfficiently: (targetElement, newHTML) => {
      if (!targetElement) return;
      
      const fragment = document.createDocumentFragment();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = newHTML;
      
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }
      
      // Use batch update
      requestAnimationFrame(() => {
        targetElement.innerHTML = '';
        targetElement.appendChild(fragment);
      });
    },

    // Preload resources for better performance
    preloadResource: (url, type = 'fetch') => {
      if (type === 'fetch') {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        document.head.appendChild(link);
      }
    }
  };

  // ========================================
  // URL UTILITIES (Using Native URLSearchParams)
  // ========================================
  
  const UrlUtils = {
    /**
     * Parse URL and return URL object with searchParams
     * @param {string|URL} url - URL string or URL object
     * @returns {URL} URL object with parsed searchParams
     */
    parseUrl(url) {
      try {
        if (url instanceof URL) {
          return new URL(url);
        }
        return new URL(url, window.location.origin);
      } catch (e) {
        // Fallback for relative URLs
        return new URL(url, window.location.href);
      }
    },

    /**
     * Get base URL without query parameters
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {string} Base URL without query string
     */
    getBaseUrl(url = window.location.href) {
      const urlObj = this.parseUrl(url);
      return urlObj.origin + urlObj.pathname;
    },

    /**
     * Get query parameters as URLSearchParams object
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {URLSearchParams} URLSearchParams object
     */
    getSearchParams(url = window.location.href) {
      const urlObj = this.parseUrl(url);
      return urlObj.searchParams;
    },

    /**
     * Get a specific query parameter value
     * @param {string} key - Parameter name
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {string|null} Parameter value or null
     */
    getParam(key, url = window.location.href) {
      const searchParams = this.getSearchParams(url);
      return searchParams.get(key);
    },

    /**
     * Set a query parameter value
     * @param {string} key - Parameter name
     * @param {string} value - Parameter value
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {string} Updated URL string
     */
    setParam(key, value, url = window.location.href) {
      const urlObj = this.parseUrl(url);
      urlObj.searchParams.set(key, value);
      return urlObj.pathname + urlObj.search;
    },

    /**
     * Delete a query parameter
     * @param {string} key - Parameter name
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {string} Updated URL string
     */
    deleteParam(key, url = window.location.href) {
      const urlObj = this.parseUrl(url);
      urlObj.searchParams.delete(key);
      return urlObj.pathname + urlObj.search;
    },

    /**
     * Build URL with multiple query parameters
     * @param {string|URL} baseUrl - Base URL
     * @param {Object} params - Object with key-value pairs for query parameters
     * @param {boolean} preserveExisting - Whether to preserve existing query params (default: true)
     * @returns {string} URL string with query parameters
     */
    buildUrl(baseUrl, params = {}, preserveExisting = true) {
      const urlObj = this.parseUrl(baseUrl);
      
      if (!preserveExisting) {
        urlObj.search = '';
      }
      
      // Use URLSearchParams to efficiently set multiple parameters
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          urlObj.searchParams.delete(key);
        } else {
          urlObj.searchParams.set(key, String(value));
        }
      });
      
      return urlObj.pathname + urlObj.search;
    },

    /**
     * Parse filter parameter from URL (e.g., "filter.v.availability=1")
     * Handles both query string format and path-based filters
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {Object} Object with filter parameters
     */
    parseFilterParams(url = window.location.href) {
      const urlObj = this.parseUrl(url);
      const searchParams = urlObj.searchParams;
      const filters = {};
      
      // Parse query string filters (e.g., ?filter.v.availability=1)
      for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter.')) {
          filters[key] = value;
        }
      }
      
      return filters;
    },

    /**
     * Build URL with sort_by parameter using URLSearchParams
     * @param {string} sortValue - Sort value (e.g., "price-ascending" or "filter.v.availability=1")
     * @param {string|URL} baseUrl - Base URL (defaults to current location)
     * @returns {string} URL string with sort parameter
     */
    buildSortUrl(sortValue, baseUrl = window.location.href) {
      const urlObj = this.parseUrl(baseUrl);
      
      // Handle availability filter (special case)
      if (sortValue === 'filter.v.availability=1') {
        urlObj.searchParams.set('filter.v.availability', '1');
        // Remove sort_by if it exists
        urlObj.searchParams.delete('sort_by');
      } else {
        // Regular sort option
        urlObj.searchParams.set('sort_by', sortValue);
        // Remove availability filter if it exists
        urlObj.searchParams.delete('filter.v.availability');
      }
      
      return urlObj.pathname + urlObj.search;
    },

    /**
     * Query params tied to filtering/pagination state (stripped when applying or clearing tag filters).
     */
    isFilterQueryParam(key) {
      return key === 'page' || (typeof key === 'string' && key.indexOf('filter.') === 0);
    },

    /**
     * Copy current query string minus filter-specific params (keeps sort_by, utm_*, etc.).
     */
    copyPreservedSearchParams(referenceUrl = window.location.href) {
      const ref = this.parseUrl(referenceUrl);
      const preserved = new URLSearchParams();

      ref.searchParams.forEach((value, key) => {
        if (!this.isFilterQueryParam(key)) {
          preserved.set(key, value);
        }
      });

      return preserved;
    },

    /**
     * Attach preserved non-filter query params to a path.
     * @param {string} pathname - e.g. /collections/handle or /collections/handle/tag1+tag2
     * @param {string|URL} referenceUrl - source for params to preserve
     * @param {{ page?: number|null }} options - optional page for pagination (omitted or 1 removes page)
     */
    buildUrlWithPreservedQuery(pathname, referenceUrl = window.location.href, options = {}) {
      const params = this.copyPreservedSearchParams(referenceUrl);
      const path = pathname.startsWith('/') ? pathname : '/' + pathname;

      if (options.page != null) {
        const pageNum = parseInt(options.page, 10);
        if (pageNum > 1) {
          params.set('page', String(pageNum));
        } else {
          params.delete('page');
        }
      }

      const qs = params.toString();
      return qs ? path + '?' + qs : path;
    },

    /**
     * Base collection path (no tag segments) with non-filter query params preserved.
     */
    buildClearedCollectionUrl(referenceUrl = window.location.href) {
      const ref = this.parseUrl(referenceUrl);
      const parts = ref.pathname.split('/').filter(Boolean);
      let basePath = ref.pathname;

      if (parts.length >= 2 && parts[0] === 'collections') {
        basePath = '/collections/' + parts[1];
      }

      return this.buildUrlWithPreservedQuery(basePath, referenceUrl);
    },

    /**
     * Build URL preserving ALL query params except `page`.
     * Unlike buildUrlWithPreservedQuery, this keeps filter.* params (e.g. filter.v.availability)
     * so OR-merge per-tag fetches and pagination URLs include active facet filters.
     * @param {string} pathname - e.g. /collections/handle/tag
     * @param {string|URL} referenceUrl - source URL to copy params from
     * @param {{ page?: number|null }} options
     */
    buildUrlWithAllQuery(pathname, referenceUrl = window.location.href, options = {}) {
      const ref = this.parseUrl(referenceUrl);
      const params = new URLSearchParams();

      ref.searchParams.forEach((value, key) => {
        if (key !== 'page') params.set(key, value);
      });

      if (options.page != null) {
        const pageNum = parseInt(options.page, 10);
        if (pageNum > 1) params.set('page', String(pageNum));
        else params.delete('page');
      }

      const path = pathname.startsWith('/') ? pathname : '/' + pathname;
      const qs = params.toString();
      return qs ? path + '?' + qs : path;
    },

    /**
     * Per-tag fetch URLs for OR multi-filter merges (each tag path + all query params including filter.*).
     * @param {string} collectionBaseUrl - e.g. /collections/handle
     * @param {string[]} tags - tag path segments
     * @param {string|URL} referenceUrl - full filter URL or location to copy query from
     */
    buildSingleTagFetchUrls(collectionBaseUrl, tags, referenceUrl = window.location.href) {
      return tags.map((tag) =>
        this.buildUrlWithAllQuery(`${collectionBaseUrl}/${tag}`, referenceUrl)
      );
    }
  };

  // ========================================
  // ADVANCED CACHING SYSTEM
  // ========================================
  
  const CacheManager = {
    cache: new Map(),
    cacheExpiry: 20 * 60 * 1000, // 20 minutes for collection/filter pages (balance of freshness and fewer requests)
    
    generateKey: (url, params = {}) => {
      const keyParams = new URLSearchParams(params).toString();
      return `${url}${keyParams ? '?' + keyParams : ''}`;
    },
    
    set: function(key, data) {
      const item = {
        data,
        timestamp: Date.now(),
        expires: Date.now() + CacheManager.cacheExpiry
      };
      CacheManager.cache.set(key, item);
    },
    
    get: function(key) {
      const item = CacheManager.cache.get(key);
      if (!item) return null;
      
      if (Date.now() > item.expires) {
        CacheManager.cache.delete(key);
        return null;
      }
      return item.data;
    },
    
    clear: function() {
      CacheManager.cache.clear();
    },

    // Clean expired entries
    cleanup: function() {
      const now = Date.now();
      for (const [key, item] of CacheManager.cache.entries()) {
        if (now > item.expires) {
          CacheManager.cache.delete(key);
        }
      }
    }
  };

  // ========================================
  // GLOBAL EXPORTS
  // ========================================
  
  // Expose utilities globally for other filter modules
  window.FilterUtils = { CONFIG, Utils, UrlUtils };
  window.CacheManager = CacheManager;

})();
